import { useRouter } from "@/_router";
import { useUserSlice } from "@/models/useUserSlice";
import { useUISlice } from "@/models/useUISlice";
import { useEffect, useRef, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronLeftIcon,
  HeartIcon,
  HeartFilledIcon,
  LibraryIcon,
  MoonIcon,
  SunIcon,
  CloseIcon,
  FlagIcon,
  ShareIcon,
} from "@/components/Icons";
import { contactUs } from "@/models/utils";
import toast from "react-hot-toast";
import ChapterGenerationStatus from "@/components/ChapterGenerationStatus";
import ReaderSettingsSheet from "@/components/ReaderSettingsSheet";
import Skeleton from "@/components/Skeleton";
import NativeContainer from "@/components/NativeContainer";
import { useAuth } from "@/models/useAuth";
import ReactMarkdown from "react-markdown";
import { updateStatusBarStyle } from "@/models/useTheme";
import { useDismissSheet } from "@/models/useDismissSheet";
import { useAndroidBackButton } from "@/models/useAndroidBackButton";
import { LocalNotifications } from "@capacitor/local-notifications";
import { isNativeApp } from "@/models/platform";
import { logFunnelEvent } from "@/fetchData/datadog";
import { requestReview, shareBook } from "@/models/useNative";

const READER_THEMES = {
  default: { bg: "var(--bg-primary)", text: "var(--text-primary)" },
  sepia: { bg: "#f4ecd8", text: "#5b4636" },
  dark: { bg: "#1a1a1a", text: "#d4d4d4" },
} as const;

const COLUMN_GAP = 48;

const getReaderBgHex = (readerTheme: keyof typeof READER_THEMES): string => {
  if (readerTheme === "sepia") return "#f4ecd8";
  if (readerTheme === "dark") return "#1a1a1a";
  return document.documentElement.dataset.theme === "dark"
    ? "#000000"
    : "#FFFFFF";
};

const REPORT_REASONS = [
  "offensive",
  "sexual",
  "harmful",
  "copyright",
  "other",
] as const;

const ReportModal = ({
  bookTitle,
  bookId,
  chapterNumber,
  onClose,
}: {
  bookTitle: string;
  bookId: string;
  chapterNumber: number;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState<string>("");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = () => {
    if (!reason) return;
    setSubmitting(true);
    // Send via email in the background using contactUs as a fallback
    contactUs(
      `Report: ${bookTitle}`,
      `Book: ${bookTitle} (ID: ${bookId}, Chapter: ${chapterNumber})%0AReason: ${t(`reader.reportReasons.${reason}`)}%0ADetails: ${details}`
    );
    toast.success(t("reader.reportSuccess"));
    onClose();
  };

  return (
    <>
      <div
        className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="fixed bottom-0 left-0 right-0 z-[101] max-h-[85dvh] bg-[var(--glass-bg)] backdrop-blur-3xl rounded-t-[40px] overflow-y-auto shadow-2xl">
        <div className="pt-6 pb-4 px-6 sticky top-0 bg-[var(--glass-bg)] backdrop-blur-3xl z-10 flex items-center justify-between">
          <h3 className="text-[var(--text-primary)] font-bold text-2xl tracking-tight">
            {t("reader.reportTitle")}
          </h3>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-black/5 dark:bg-white/10 flex items-center justify-center active:scale-90 transition-transform"
          >
            <CloseIcon size={20} />
          </button>
        </div>
        <div className="px-6 pt-2 pb-[calc(env(safe-area-inset-bottom,24px)+24px)]">
          <div className="flex flex-col gap-2 mb-4">
            {REPORT_REASONS.map((key) => (
              <button
                key={key}
                onClick={() => setReason(key)}
                className={`text-left px-4 py-3 rounded-2xl text-base font-medium transition-colors ${
                  reason === key
                    ? "bg-[var(--accent)]/15 text-[var(--accent)]"
                    : "text-[var(--text-primary)] bg-black/5 dark:bg-white/5"
                }`}
              >
                {t(`reader.reportReasons.${key}`)}
              </button>
            ))}
          </div>
          <textarea
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder={t("reader.reportPlaceholder")}
            className="w-full h-24 rounded-2xl bg-black/5 dark:bg-white/5 text-[var(--text-primary)] placeholder:text-[var(--text-muted)] px-4 py-3 text-base resize-none mb-4"
          />
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 h-14 rounded-full bg-black/5 dark:bg-white/5 text-[var(--text-secondary)] font-semibold text-base active:scale-[0.97] transition-transform"
            >
              {t("reader.reportCancel")}
            </button>
            <button
              onClick={handleSubmit}
              disabled={!reason || submitting}
              className="flex-1 h-14 rounded-full bg-pink-500 text-white font-semibold text-base active:scale-[0.97] transition-transform disabled:opacity-40"
            >
              {t("reader.reportSubmit")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const Reader = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const currentChapter = useUserSlice((state) => state.currentChapter);
  const currentChapterStatus = useUserSlice(
    (state) => state.currentChapterStatus
  );
  const currentBook = useUserSlice((state) => state.currentBook);
  const isSubscribed = useUserSlice((state) => state.isSubscribed);
  const readingProgress = useUserSlice((state) => state.readingProgress);
  const readerSettings = useUserSlice((state) => state.readerSettings);
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  const likedBooks = useUserSlice((state) => state.likedBooks);
  const hasSeenReaderTutorial = useUserSlice(
    (state) => state.hasSeenReaderTutorial
  );

  const [chromeVisible, setChromeVisible] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [contentsOpen, setContentsOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0); // 0 = hidden, 1 = step 1, 2 = step 2

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [containerWidth, setContainerWidth] = useState(0);
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const prevReaderTheme = useRef(readerSettings.readerTheme);
  const openAtLastRef = useRef(false);

  const params = new URLSearchParams(window.location.search);
  const bookId = params.get("bookId");
  const chapterNumber = parseInt(params.get("chapter") || "1");
  const initialPage = params.get("page");

  // Set once on mount if page=last
  useEffect(() => {
    if (initialPage === "last") {
      openAtLastRef.current = true;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const chapterCount = currentBook?.chapterCount || 0;
  const freeChapterLimit = currentBook?.paywallAfterChapter || 10;

  const theme = READER_THEMES[readerSettings.readerTheme];

  const isLiked = bookId ? likedBooks.includes(bookId) : false;

  const toggleLiked = useCallback(() => {
    if (!bookId) return;
    const pref = useUserSlice.getState().preference();
    if (isLiked) {
      pref.removeFromLikedBooks(bookId);
    } else {
      pref.addToLikedBooks(bookId);
    }
  }, [bookId, isLiked]);

  const reportBook = useCallback(() => {
    setReportOpen(true);
  }, []);

  const toggleDarkMode = useCallback(() => {
    const pref = useUserSlice.getState().preference();
    const current = readerSettings.readerTheme;
    pref.setReaderSettings({
      readerTheme: current === "dark" ? "sepia" : "dark",
    });
  }, [readerSettings.readerTheme]);

  useDismissSheet(
    settingsOpen,
    useCallback(() => setSettingsOpen(false), [])
  );
  useDismissSheet(
    contentsOpen,
    useCallback(() => setContentsOpen(false), [])
  );
  useDismissSheet(
    reportOpen,
    useCallback(() => setReportOpen(false), [])
  );

  const backFallback = useCallback(() => {
    router.replace(`/native/home`);
  }, [router]);
  useAndroidBackButton(backFallback);

  const goToChapter = useCallback(
    (num: number) => {
      if (num < 1 || num > chapterCount) return;
      if (num > freeChapterLimit && !useAuth.isAuthenticated()) {
        useAuth.signIn({
          callback: () => {
            if (!useUserSlice.getState().isSubscribed) {
              useUISlice.getState().setPaywallVisible({
                subtitle: t("reader.subscriptionRequired"),
                utmSource: "reader_chapter_gate",
                callback: () => {
                  if (useUserSlice.getState().isSubscribed) {
                    router.replace(
                      `/native/reader?bookId=${bookId}&chapter=${num}`
                    );
                  }
                },
              });
              return;
            }
            router.replace(`/native/reader?bookId=${bookId}&chapter=${num}`);
          },
        });
        return;
      }
      if (num > freeChapterLimit && !isSubscribed) {
        useUISlice.getState().setPaywallVisible({
          subtitle: t("reader.subscriptionRequired"),
          utmSource: "reader_chapter_gate",
          callback: () => {
            if (useUserSlice.getState().isSubscribed) {
              router.replace(`/native/reader?bookId=${bookId}&chapter=${num}`);
            }
          },
        });
        return;
      }
      router.replace(`/native/reader?bookId=${bookId}&chapter=${num}`);
    },
    [bookId, chapterCount, isSubscribed, freeChapterLimit, router, t]
  );

  // Page navigation
  const goToNextPage = useCallback(() => {
    if (currentPage < totalPages - 1) {
      setCurrentPage((p) => p + 1);
    } else {
      requestReview();
      if (!isSubscribed && chapterNumber + 1 <= chapterCount) {
        const nextChapterPastLimit = chapterNumber + 1 > freeChapterLimit;
        useUISlice.getState().setPaywallVisible({
          ...(nextChapterPastLimit && { subtitle: t("reader.subscriptionRequired") }),
          utmSource: "reader_next_chapter",
          callback: () => {
            goToChapter(chapterNumber + 1);
          },
        });
        return;
      }
      goToChapter(chapterNumber + 1);
    }
  }, [currentPage, totalPages, goToChapter, chapterNumber, isSubscribed, chapterCount, t]);

  const goToPrevPage = useCallback(() => {
    if (currentPage > 0) {
      setCurrentPage((p) => p - 1);
    } else if (chapterNumber > 1) {
      router.replace(
        `/native/reader?bookId=${bookId}&chapter=${chapterNumber - 1}&page=last`
      );
    }
  }, [currentPage, chapterNumber, bookId, router]);

  // Tap handler: left 30% prev, right 30% next, center 40% toggle chrome
  const handleContentTap = useCallback(
    (e: React.MouseEvent) => {
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) return;

      // Don't handle taps on buttons or links
      if ((e.target as HTMLElement).closest("button, a")) return;

      const screenWidth = window.innerWidth;
      const x = e.clientX;
      const leftZone = screenWidth * 0.3;
      const rightZone = screenWidth * 0.7;

      if (x < leftZone) {
        goToPrevPage();
      } else if (x > rightZone) {
        goToNextPage();
      } else {
        setChromeVisible((v) => !v);
      }
    },
    [goToPrevPage, goToNextPage]
  );

  // Fetch chapter
  useEffect(() => {
    if (!bookId) return;

    if (!currentBook || currentBook.bookId !== bookId) {
      useUserSlice.getState().book().get(bookId);
    }

    // Client-side gate: non-authenticated or non-subscribed users on chapter > freeChapterLimit get redirected
    if (
      chapterNumber > freeChapterLimit &&
      (!useAuth.isAuthenticated() || !isSubscribed)
    ) {
      router.replace(`/native/reader?bookId=${bookId}&chapter=1`);
      return;
    }

    useUserSlice.getState().chapter().get({ bookId, chapterNumber });

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId, chapterNumber]);

  // Poll when generating
  useEffect(() => {
    if (currentChapterStatus === "generating" && bookId) {
      pollRef.current = setInterval(() => {
        useUserSlice.getState().chapter().pollStatus({ bookId, chapterNumber });
      }, 30000);

      return () => {
        if (pollRef.current) clearInterval(pollRef.current);
      };
    }
  }, [currentChapterStatus, bookId, chapterNumber]);

  // Server-side fallback: if server returns subscription_required, redirect to chapter 1
  useEffect(() => {
    if (currentChapterStatus === "subscription_required") {
      router.replace(`/native/reader?bookId=${bookId}&chapter=1`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChapterStatus]);

  const isContentReady =
    currentChapterStatus === "ready" &&
    currentChapter &&
    currentChapter.bookId === bookId &&
    currentChapter.chapterNumber === chapterNumber;

  // Track chapter opened
  useEffect(() => {
    if (!isContentReady || !bookId) return;
    logFunnelEvent("[8] reader:chapter_opened", {
      bookId,
      chapterNumber,
      isSubscribed,
    });
  }, [isContentReady, bookId, chapterNumber, isSubscribed]);

  // Reset page to 0 on chapter change (unless navigating to last page)
  useEffect(() => {
    if (!openAtLastRef.current) {
      setCurrentPage(0);
    }
  }, [chapterNumber]);

  // Measure container width
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Recalculate total pages when content or container changes
  useEffect(() => {
    const el = contentRef.current;
    if (!el || containerWidth === 0) return;

    const recalc = () => {
      const newTotal = Math.max(1, Math.ceil(el.scrollWidth / containerWidth));
      setTotalPages((prev) => {
        if (prev === newTotal && !openAtLastRef.current) return prev;
        if (openAtLastRef.current) {
          openAtLastRef.current = false;
          setCurrentPage(newTotal - 1);
        } else {
          setCurrentPage((currentP) => {
            const fraction = prev > 1 ? currentP / (prev - 1) : 0;
            const newPage = Math.round(fraction * (newTotal - 1));
            return Math.min(newPage, newTotal - 1);
          });
        }
        return newTotal;
      });
    };

    // Double rAF to ensure column layout is fully settled
    requestAnimationFrame(() => requestAnimationFrame(recalc));

    const observer = new ResizeObserver(() => {
      requestAnimationFrame(recalc);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [
    containerWidth,
    isContentReady,
    chapterNumber,
    readerSettings.fontSize,
    readerSettings.lineHeight,
    readerSettings.fontFamily,
  ]);

  // Restore page position from persisted reading progress
  useEffect(() => {
    if (currentChapterStatus === "ready" && bookId && totalPages > 1) {
      const progress = readingProgress[bookId];
      if (progress?.lastChapter === chapterNumber && progress.scrollPosition) {
        setCurrentPage(Math.min(progress.scrollPosition, totalPages - 1));
      }
    }
  }, [
    currentChapterStatus,
    bookId,
    chapterNumber,
    readingProgress,
    totalPages,
  ]);

  // iOS Safari: reload after reader theme change so Safari re-reads meta theme-color
  useEffect(() => {
    if (prevReaderTheme.current === readerSettings.readerTheme) return;
    prevReaderTheme.current = readerSettings.readerTheme;

    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    if (!isIOS) return;

    const timer = setTimeout(() => {
      // Save current page to store immediately so it restores after reload
      if (bookId) {
        useUserSlice.getState().preference().saveProgress({
          bookId,
          chapterNumber,
          scrollPosition: currentPage,
        });
      }
      location.reload();
    }, 500);
    return () => clearTimeout(timer);
  }, [readerSettings.readerTheme, bookId, chapterNumber, currentPage]);

  // Prefetch next chapter when nearing the end (within last 3 pages)
  useEffect(() => {
    if (currentChapterStatus !== "ready" || !bookId) return;
    if (chapterNumber >= chapterCount) return;
    if (totalPages - currentPage > 3) return;

    useUserSlice
      .getState()
      .chapter()
      .prefetch({ bookId, chapterNumber: chapterNumber + 1 });
  }, [
    currentChapterStatus,
    bookId,
    chapterNumber,
    chapterCount,
    currentPage,
    totalPages,
  ]);

  // Save reading progress on page change (debounced)
  const savePageDebounced = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!bookId || currentChapterStatus !== "ready") return;

    if (savePageDebounced.current) clearTimeout(savePageDebounced.current);
    savePageDebounced.current = setTimeout(() => {
      useUserSlice.getState().preference().saveProgress({
        bookId,
        chapterNumber,
        scrollPosition: currentPage,
      });
    }, 500);

    return () => {
      if (savePageDebounced.current) clearTimeout(savePageDebounced.current);
    };
  }, [bookId, chapterNumber, currentPage, currentChapterStatus]);

  // Update native status bar to match reader theme on mount and when theme/content changes
  useEffect(() => {
    if (!isContentReady) return;
    const timeout = setTimeout(() => {
      updateStatusBarStyle(
        readerSettings.readerTheme === "dark" ? "dark" : "light",
        getReaderBgHex(readerSettings.readerTheme)
      );
    }, 300);
    return () => clearTimeout(timeout);
  }, [readerSettings.readerTheme, isContentReady]);

  // Show reader tutorial on first read
  useEffect(() => {
    if (isContentReady && !hasSeenReaderTutorial) {
      setTutorialStep(1);
    }
  }, [isContentReady, hasSeenReaderTutorial]);

  const dismissTutorial = useCallback(() => {
    setTutorialStep(0);
    useUserSlice.getState().preference().setHasSeenReaderTutorial();
  }, []);

  // Prevent body scrolling
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // Show a persistent notification so user can tap to return to reading
  const READING_NOTIFICATION_ID = 9999;
  useEffect(() => {
    if (!isNativeApp() || !isContentReady || !currentBook) return;

    const showNotification = async () => {
      const perm = await LocalNotifications.requestPermissions();
      if (perm.display !== "granted") return;

      await LocalNotifications.schedule({
        notifications: [
          {
            id: READING_NOTIFICATION_ID,
            title: t("reader.readingNotification"),
            body: currentBook.aiTitle || "",
            ongoing: true,
            autoCancel: false,
            extra: {
              bookId,
              chapter: String(chapterNumber),
            },
          },
        ],
      });
    };

    showNotification();

    return () => {
      LocalNotifications.cancel({
        notifications: [{ id: READING_NOTIFICATION_ID }],
      });
    };
  }, [isContentReady, currentBook, bookId, chapterNumber, t]);

  return (
    <NativeContainer
      className="h-[100dvh] relative overflow-hidden"
      style={{ backgroundColor: theme.bg, color: theme.text }}
      themeColor={getReaderBgHex(readerSettings.readerTheme)}
    >
      {/* Header — tap-controlled */}
      <header
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
          chromeVisible
            ? "translate-y-0 opacity-100"
            : "-translate-y-full opacity-0"
        }`}
      >
        <div
          className="flex items-center justify-between px-4 py-2 pt-[calc(env(safe-area-inset-top)+12px)] backdrop-blur-3xl w-full"
          style={{ backgroundColor: `${theme.bg}99` }}
        >
          <button
            onClick={() => router.replace(`/native/home`)}
            className="w-14 h-14 rounded-full flex items-center justify-center active:scale-90 transition-transform"
          >
            <ChevronLeftIcon size={32} />
          </button>
          <p className="text-sm font-semibold opacity-80 truncate max-w-[200px]">
            {currentBook?.aiTitle || ""}
          </p>
          <div className="flex items-center">
            <button
              onClick={() => currentBook && shareBook(currentBook)}
              className="w-12 h-14 rounded-full flex items-center justify-center active:scale-90 transition-transform opacity-60"
            >
              <ShareIcon size={22} />
            </button>
            <button
              onClick={reportBook}
              className="w-12 h-14 rounded-full flex items-center justify-center active:scale-90 transition-transform opacity-60"
            >
              <FlagIcon size={22} />
            </button>
            <button
              onClick={toggleLiked}
              className="w-14 h-14 rounded-full flex items-center justify-center active:scale-90 transition-transform"
            >
              {isLiked ? (
                <HeartFilledIcon size={26} className="text-red-500" />
              ) : (
                <HeartIcon size={26} />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Paginated Content */}
      <div
        ref={viewportRef}
        className="absolute left-0 right-0 overflow-hidden"
        style={{
          top: "calc(env(safe-area-inset-top) + 20px)",
          bottom: "calc(env(safe-area-inset-bottom) + 16px)",
        }}
        onClick={handleContentTap}
      >
        {isContentReady ? (
          <div
            ref={contentRef}
            style={{
              columnWidth:
                containerWidth > 0
                  ? `${containerWidth - COLUMN_GAP}px`
                  : "100vw",
              columnGap: `${COLUMN_GAP}px`,
              columnFill: "auto",
              height: "100%",
              transform: `translateX(-${currentPage * containerWidth}px)`,
              transition: "transform 300ms ease-out",
              paddingLeft: COLUMN_GAP / 2,
              paddingRight: COLUMN_GAP / 2,
              paddingTop: 16,
              paddingBottom: 16,
              fontSize: `${readerSettings.fontSize}px`,
              lineHeight: readerSettings.lineHeight,
              fontFamily:
                readerSettings.fontFamily === "serif"
                  ? "Georgia, serif"
                  : readerSettings.fontFamily === "monospace"
                    ? "monospace"
                    : "system-ui, sans-serif",
            }}
          >
            <ReactMarkdown
              components={{
                p: ({ children }) => <p className="mb-6">{children}</p>,
                h1: ({ children }) => (
                  <h1 className="text-2xl font-bold mb-4 mt-8">{children}</h1>
                ),
                h2: ({ children }) => (
                  <h2 className="text-xl font-bold mb-3 mt-6">{children}</h2>
                ),
                h3: ({ children }) => (
                  <h3 className="text-lg font-semibold mb-2 mt-4">
                    {children}
                  </h3>
                ),
                em: ({ children }) => <em className="italic">{children}</em>,
                strong: ({ children }) => (
                  <strong className="font-bold">{children}</strong>
                ),
                hr: () => <hr className="my-8 border-current opacity-10" />,
              }}
            >
              {currentChapter.aiContent}
            </ReactMarkdown>
          </div>
        ) : currentChapterStatus === "subscription_required" ? (
          <Skeleton variant="Text" />
        ) : currentChapterStatus ? (
          <ChapterGenerationStatus
            status={
              currentChapterStatus as
                | "generating"
                | "queued"
                | "requires_previous"
            }
            onGoToPrevious={() => goToChapter(chapterNumber - 1)}
          />
        ) : (
          <Skeleton variant="Text" />
        )}
      </div>

      {/* Always-visible chapter + page indicators */}
      {isContentReady && totalPages > 1 && (
        <>
          <div
            className="fixed top-0 left-0 z-30 pointer-events-none"
            style={{
              paddingTop: "calc(env(safe-area-inset-top) + 14px)",
              paddingLeft: COLUMN_GAP / 2,
            }}
          >
            <span
              className="opacity-40 tabular-nums"
              style={{ color: theme.text, fontSize: 10 }}
            >
              {t("reader.chapterLabel", { current: chapterNumber })}
            </span>
          </div>
          <div
            className="fixed bottom-0 right-0 z-30 pointer-events-none"
            style={{
              paddingBottom: "calc(env(safe-area-inset-bottom) + 8px)",
              paddingRight: 16,
            }}
          >
            <span
              className="opacity-40 tabular-nums"
              style={{ color: theme.text, fontSize: 10 }}
            >
              {currentPage + 1} / {totalPages}
            </span>
          </div>
        </>
      )}

      {/* Footer — tap-controlled */}
      <footer
        className={`fixed bottom-0 left-0 right-0 z-40 transition-all duration-300 ${
          chromeVisible
            ? "translate-y-0 opacity-100"
            : "translate-y-full opacity-0"
        }`}
      >
        <div
          className="flex items-center justify-around px-6 py-4 pb-[calc(env(safe-area-inset-bottom)+16px)] backdrop-blur-3xl"
          style={{ backgroundColor: `${theme.bg}99` }}
        >
          <button
            onClick={() => setContentsOpen(true)}
            className="flex flex-col items-center justify-center w-16 h-16 active:scale-90 transition-transform opacity-80 active:opacity-100"
          >
            <LibraryIcon size={28} />
          </button>
          <button
            onClick={() => setSettingsOpen(true)}
            className="flex flex-col items-center justify-center w-16 h-16 active:scale-90 transition-transform opacity-80 active:opacity-100"
          >
            <span
              className="font-normal leading-none tracking-wide"
              style={{ fontSize: 26 }}
            >
              Aa
            </span>
          </button>
          <button
            onClick={toggleDarkMode}
            className="flex flex-col items-center justify-center w-16 h-16 active:scale-90 transition-transform opacity-80 active:opacity-100"
          >
            {readerSettings.readerTheme === "dark" ? (
              <SunIcon size={28} />
            ) : (
              <MoonIcon size={28} />
            )}
          </button>
        </div>
      </footer>

      {/* Contents Sheet */}
      <>
        <div
          className={`fixed inset-0 z-[100] bg-[var(--bg-primary)]/50 backdrop-blur-sm transition-opacity duration-300 ${
            contentsOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          onClick={() => setContentsOpen(false)}
        />
        <div
          className={`fixed bottom-0 left-0 right-0 z-[101] max-h-[85dvh] bg-[var(--glass-bg)] backdrop-blur-3xl rounded-t-[40px] overflow-y-auto shadow-2xl transition-transform duration-300 ease-out ${
            contentsOpen ? "translate-y-0" : "translate-y-full"
          }`}
        >
          <div className="pt-6 pb-4 px-6 sticky top-0 bg-[var(--glass-bg)] backdrop-blur-3xl z-10 flex items-center justify-between">
            <h3 className="text-[var(--text-primary)] font-bold text-2xl tracking-tight">
              {t("book.chapterList")}
            </h3>
            <button
              onClick={() => setContentsOpen(false)}
              className="w-10 h-10 rounded-full bg-black/5 dark:bg-white/10 flex items-center justify-center active:scale-90 transition-transform"
            >
              <CloseIcon size={20} />
            </button>
          </div>
          <div className="px-6 pt-2 pb-[calc(env(safe-area-inset-bottom,24px)+24px)] grid grid-cols-5 gap-2">
            {Array.from({ length: chapterCount }, (_, i) => i + 1).map(
              (num) => (
                <button
                  key={num}
                  onClick={() => {
                    setContentsOpen(false);
                    goToChapter(num);
                  }}
                  className={`aspect-square rounded-xl flex items-center justify-center text-sm font-medium transition-colors ${
                    num === chapterNumber
                      ? "bg-[var(--accent)]/15 text-[var(--accent)] font-semibold"
                      : "text-[var(--text-primary)] bg-black/5 dark:bg-white/5 active:bg-[var(--glass-bg-hover)]"
                  }`}
                >
                  {num}
                  {num > freeChapterLimit && !isSubscribed && (
                    <span className="text-[8px] absolute mt-6">🔒</span>
                  )}
                </button>
              )
            )}
          </div>
        </div>
      </>

      {/* Settings Sheet */}
      <ReaderSettingsSheet
        visible={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />

      {/* Report Modal */}
      {reportOpen && (
        <ReportModal
          bookTitle={currentBook?.aiTitle || "Unknown"}
          bookId={bookId || ""}
          chapterNumber={chapterNumber}
          onClose={() => setReportOpen(false)}
        />
      )}

      {/* Reader Tutorial Overlay */}
      {tutorialStep > 0 && (
        <div className="fixed inset-0 z-[200] bg-black/70 flex flex-col items-center justify-center">
          {tutorialStep === 1 && (
            <div className="flex flex-col items-center gap-6 px-8">
              {/* Tap hand icon */}
              <svg
                width="80"
                height="80"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M8 13V4.5a1.5 1.5 0 0 1 3 0V12" />
                <path d="M11 11.5v-2a1.5 1.5 0 0 1 3 0V12" />
                <path d="M14 10.5a1.5 1.5 0 0 1 3 0V12" />
                <path d="M17 11.5a1.5 1.5 0 0 1 3 0V16a6 6 0 0 1-6 6h-2 .208a6 6 0 0 1-5.012-2.7L7 19c-.312-.479-1.407-2.388-3.286-5.728a1.5 1.5 0 0 1 .536-2.022 1.867 1.867 0 0 1 2.28.28L8 13" />
              </svg>
              <p className="text-white text-xl font-semibold text-center leading-snug">
                {t("reader.tutorialStep1Prefix")}{" "}
                <span className="text-pink-500">
                  {t("reader.tutorialStep1Highlight")}
                </span>{" "}
                {t("reader.tutorialStep1Suffix")}
              </p>
            </div>
          )}

          {tutorialStep === 2 && (
            <div className="flex flex-col items-center gap-6 px-8">
              {/* Left / Right arrows with labels */}
              <div className="flex items-center justify-between w-full px-4">
                <div className="flex flex-col items-center gap-2">
                  <ChevronLeftIcon size={48} className="text-white" />
                  <span className="text-white text-sm">
                    {t("reader.previous")}
                  </span>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <ChevronLeftIcon
                    size={48}
                    className="text-white rotate-180"
                  />
                  <span className="text-white text-sm">{t("reader.next")}</span>
                </div>
              </div>
              <p className="text-white text-xl font-semibold text-center leading-snug">
                {t("reader.tutorialStep2Prefix")}{" "}
                <span className="text-pink-500">
                  {t("reader.tutorialStep2Highlight")}
                </span>{" "}
                {t("reader.tutorialStep2Suffix")}
              </p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex flex-col items-center gap-3 mt-10 w-full px-12">
            {tutorialStep === 1 ? (
              <button
                onClick={() => setTutorialStep(2)}
                className="w-full py-4 rounded-full bg-pink-500 text-white font-semibold text-base active:scale-[0.97] transition-transform"
              >
                {t("reader.tutorialNext", { current: 1, total: 2 })}
              </button>
            ) : (
              <button
                onClick={dismissTutorial}
                className="w-full py-4 rounded-full bg-pink-500 text-white font-semibold text-base active:scale-[0.97] transition-transform"
              >
                {t("reader.tutorialGotIt")}
              </button>
            )}
          </div>

          {/* Dot indicators */}
          <div className="flex gap-2 mt-6">
            <div
              className={`w-2 h-2 rounded-full ${tutorialStep === 1 ? "bg-white" : "bg-white/30"}`}
            />
            <div
              className={`w-2 h-2 rounded-full ${tutorialStep === 2 ? "bg-white" : "bg-white/30"}`}
            />
          </div>
        </div>
      )}
    </NativeContainer>
  );
};

export default Reader;
